import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'react-feather';

interface TagFilterProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  disabled?: boolean;
}

export const TagFilter: React.FC<TagFilterProps> = ({
  selectedTags,
  onTagsChange,
  availableTags,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Toggle tag selection (multi-select checkbox behavior)
  // Supports OR logic: teachings matching ANY selected tag will be shown
  const handleToggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
      >
        <span>Tags</span>
        {selectedTags.length > 0 && (
          <span className="bg-eddy-blue text-white rounded-full px-2 py-0.5 text-xs font-medium">
            {selectedTags.length}
          </span>
        )}
        <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-[200px] max-h-64 overflow-y-auto">
          {availableTags.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 text-sm">
              No tags available
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {availableTags.map(tag => (
                <label key={tag} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleToggleTag(tag)}
                    className="w-4 h-4 accent-eddy-blue rounded"
                  />
                  <span className="text-sm">{tag}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Display selected tags */}
      {selectedTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedTags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 bg-eddy-blue/10 text-eddy-blue border border-eddy-blue/20 rounded-full text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleToggleTag(tag)}
                className="ml-1 hover:text-eddy-blue/70"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
